import type { MissionPanelFallback } from './missionTypes.js';
import { EVENT_CREATE_WIZARD_LAST_INDEX } from '../events/eventCreateWizard.js';

export interface MissionFallbackAction {
  type: MissionPanelFallback;
  label: string;
  /** CustomEvent name for dispatch fallbacks */
  event?: string;
  detail?: Record<string, unknown>;
  /** Client route for navigate fallbacks */
  navigateTo?: string;
  /** Programmatic click on a selector */
  clickSelector?: string;
}

const COORDINATOR_TAB_ANCHOR_SLUG: Record<string, string> = {
  'coordinator-tab-events': 'events',
  'coordinator-tab-members': 'members',
  'coordinator-tab-venues': 'venues',
  'coordinator-tab-embed': 'embed',
  'coordinator-tab-settings': 'settings',
  'coordinator-tab-certificates': 'certificates',
  'coordinator-tab-approvals': 'approvals',
  'coordinator-tab-approval-settings': 'approval-settings',
  'coordinator-tab-outside-training': 'outside-training',
  'coordinator-tab-payouts': 'payouts',
};

const EMBED_TAB_ANCHOR_SLUG: Record<string, string> = {
  'embed-tab-settings': 'settings',
  'embed-tab-code': 'code',
  'embed-tab-preview': 'preview',
};

export const MISSION_FALLBACK_REGISTRY: Record<string, MissionFallbackAction> = {
  'coordinator-create-event-btn': {
    type: 'navigate',
    label: 'Open event builder',
    navigateTo: '/coordinator-event-create?learningLab=1',
  },
  'coordinator-create-user-btn': {
    type: 'open-modal',
    label: 'Open add member dialog',
    event: 'apt-tour:open-create-user-modal',
  },
  'coordinator-venue-create-btn': {
    type: 'dispatch',
    label: 'Create venue',
    clickSelector: '[data-tour="coordinator-venue-create-btn"]',
  },
  'coordinator-agency-save-btn': {
    type: 'dispatch',
    label: 'Save agency profile',
    clickSelector: '[data-tour="coordinator-agency-save-btn"]',
  },
  'coordinator-event-cancel-btn': {
    type: 'open-modal',
    label: 'Open cancel dialog',
    event: 'apt-tour:open-event-cancel-modal',
  },
  'coordinator-event-cancel-confirm': {
    type: 'dispatch',
    label: 'Confirm cancellation',
    clickSelector: '[data-tour="coordinator-event-cancel-confirm"]',
  },
  'coordinator-event-cancel-submit-btn': {
    type: 'dispatch',
    label: 'Submit cancellation',
    clickSelector: '[data-tour="coordinator-event-cancel-submit-btn"]',
  },
  'coordinator-cert-issued-list': {
    type: 'dispatch',
    label: 'Open issued certificates',
    clickSelector: '[data-tour="coordinator-cert-issued-list"]',
  },
  'coordinator-outside-training-review-btn': {
    type: 'dispatch',
    label: 'Open outside training review',
    clickSelector: '[data-tour="coordinator-outside-training-review-btn"]',
  },
  'coordinator-outside-training-review-submit-btn': {
    type: 'dispatch',
    label: 'Submit outside training review',
    clickSelector: '[data-tour="coordinator-outside-training-review-submit-btn"]',
  },
  'event-form-publish': {
    type: 'dispatch',
    label: 'Go to publish step',
    event: 'apt-tour:stepper-step-changed',
    detail: { stepIndex: EVENT_CREATE_WIZARD_LAST_INDEX, requested: true },
  },
};

const EVENT_FORM_NAVIGATE_FALLBACK: MissionFallbackAction = {
  type: 'navigate',
  label: 'Open event builder',
  navigateTo: '/coordinator-event-create?learningLab=1',
};

export function getMissionFallbackForAnchor(
  anchor: string | undefined,
): MissionFallbackAction | null {
  if (!anchor) {
    return null;
  }
  if (MISSION_FALLBACK_REGISTRY[anchor]) {
    return MISSION_FALLBACK_REGISTRY[anchor];
  }
  if (anchor.startsWith('event-form-')) {
    return EVENT_FORM_NAVIGATE_FALLBACK;
  }
  const tabSlug = COORDINATOR_TAB_ANCHOR_SLUG[anchor];
  if (tabSlug) {
    return {
      type: 'dispatch',
      label: 'Switch tab',
      event: 'apt-tour:switch-dashboard-module-tab',
      detail: { module: 'coordinator', coordinatorTabSlug: tabSlug },
    };
  }
  const embedTab = EMBED_TAB_ANCHOR_SLUG[anchor];
  if (embedTab) {
    return {
      type: 'dispatch',
      label: 'Switch embed tab',
      event: 'apt-tour:set-embed-tab',
      detail: { tab: embedTab },
    };
  }
  return null;
}

const TOUR_NAVIGATE_EVENT = 'apt-tour:navigate';
const TOUR_STEPPER_EVENT = 'apt-tour:stepper-step-changed';

function executeEventFormPublishStepFallback(): boolean {
  const path = window.location.pathname;
  if (!path.includes('/coordinator-event')) {
    const editMatch = path.match(/\/coordinator-event-edit\/(\d+)/);
    const targetPath = editMatch
      ? `/coordinator-event-edit/${editMatch[1]}?learningLab=1`
      : '/coordinator-event-create?learningLab=1';
    window.dispatchEvent(
      new CustomEvent(TOUR_NAVIGATE_EVENT, { detail: { path: targetPath } }),
    );
  }
  window.dispatchEvent(
    new CustomEvent(TOUR_STEPPER_EVENT, {
      detail: { stepIndex: EVENT_CREATE_WIZARD_LAST_INDEX, requested: true },
    }),
  );
  return true;
}

export function executeMissionFallback(action: MissionFallbackAction): boolean {
  if (
    action.event === TOUR_STEPPER_EVENT &&
    (action.detail as { stepIndex?: number } | undefined)?.stepIndex ===
      EVENT_CREATE_WIZARD_LAST_INDEX
  ) {
    return executeEventFormPublishStepFallback();
  }
  if (action.type === 'navigate' && action.navigateTo) {
    window.dispatchEvent(
      new CustomEvent(TOUR_NAVIGATE_EVENT, {
        detail: { path: action.navigateTo },
      }),
    );
    return true;
  }
  if (action.type === 'open-modal' && action.event) {
    window.dispatchEvent(new CustomEvent(action.event, { detail: action.detail }));
    return true;
  }
  if (action.type === 'dispatch') {
    if (action.event) {
      window.dispatchEvent(new CustomEvent(action.event, { detail: action.detail }));
      return true;
    }
    if (action.clickSelector) {
      const el = document.querySelector(action.clickSelector);
      if (el instanceof HTMLElement) {
        el.click();
        return true;
      }
    }
  }
  return false;
}
